import { Pause, Play, RefreshCw, Users } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';

import { DELIVERY_KIND_LABELS } from './deliveryKinds';
import { ProactivityExperimentRecipientLists } from './ProactivityExperimentRecipientLists';
import {
  useSetProactivityExperimentPaused,
  useSyncProactivityExperimentCohort,
} from './useProactivityExperiments';

import type {
  ProactivityExperimentDetail,
  ProactivityExperimentSummary,
} from './proactivity-experiments-types';
import type { UseQueryResult } from '@tanstack/react-query';

type ProactivityExperimentDetailsPanelProps = {
  summary: ProactivityExperimentSummary;
  detail: UseQueryResult<ProactivityExperimentDetail>;
  onEditCohort: () => void;
};

const DetailField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="space-y-1">
    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </dt>
    <dd className="text-sm break-words text-foreground">{children}</dd>
  </div>
);

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const executionLabel = (summary: ProactivityExperimentSummary) =>
  summary.execution.kind === 'mode'
    ? `Mode: ${summary.execution.value}`
    : `Agent: ${summary.execution.value}`;

const CollapsiblePrompt = ({ prompt }: { prompt: string }) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Prompt
        </dt>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 px-2 text-xs"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? 'Hide prompt' : 'Show prompt'}
        </Button>
      </div>
      <dd
        id={panelId}
        hidden={!open}
        className="max-h-64 overflow-y-auto rounded-md border bg-muted/30 p-3 text-sm text-foreground"
      >
        <p className="whitespace-pre-wrap break-words">{prompt}</p>
      </dd>
    </div>
  );
};

export const ProactivityExperimentDetailsPanel = ({
  summary,
  detail,
  onEditCohort,
}: ProactivityExperimentDetailsPanelProps) => {
  const sync = useSyncProactivityExperimentCohort();
  const setPaused = useSetProactivityExperimentPaused();

  if (detail.isLoading) {
    return (
      <p className="px-4 py-6 text-sm text-muted-foreground">Loading...</p>
    );
  }

  if (detail.error) {
    return (
      <p className="px-4 py-6 text-sm text-destructive">
        {detail.error.message}
      </p>
    );
  }

  const experiment = detail.data;
  const recipients = experiment?.recipients ?? [];
  const revoked = summary.revoke_requested_at !== null;
  const paused = summary.paused_at !== null;

  const syncCohort = () => {
    if (
      window.confirm(
        `Sync "${summary.name}" from PostHog? Users who left the cohort lose their untouched card; new members get one.`
      )
    ) {
      sync.mutate(summary.id);
    }
  };

  return (
    <div className="grid gap-6 bg-muted/20 p-4 lg:grid-cols-2 lg:gap-8 lg:p-6">
      <section className="space-y-4">
        <h3 className="text-sm font-semibold">Experiment configuration</h3>
        <dl className="space-y-4 rounded-md border bg-background p-4">
          <DetailField label="Delivery">
            {DELIVERY_KIND_LABELS[summary.delivery_kind]}
            {summary.paused_at
              ? ` · paused since ${new Date(summary.paused_at).toLocaleString()}`
              : ''}
          </DetailField>
          <DetailField label="Visibility and billing">
            Visible to user: {yesNo(summary.visible_to_user)} · Billed to user:{' '}
            {yesNo(summary.billed_to_user)} · Enrols new signups:{' '}
            {yesNo(summary.enroll_new_signups)}
          </DetailField>
          {summary.delivery_kind === 'direct_routine' && experiment ? (
            <>
              <DetailField label="Routine title">
                {experiment.routine_title}
              </DetailField>
              <DetailField label="Recurrence rule">
                {experiment.routine_recurrence_rule}
              </DetailField>
              <DetailField label="Routine kind and agent mode">
                {experiment.routine_kind ?? 'digest'} ·{' '}
                {experiment.routine_agent_mode ?? 'regular'}
              </DetailField>
            </>
          ) : null}
          {experiment?.card_title ? (
            <DetailField label="Card title">
              {experiment.card_title}
            </DetailField>
          ) : null}
          {experiment?.card_body ? (
            <DetailField label="Card body">{experiment.card_body}</DetailField>
          ) : null}
          {experiment?.prompt ? (
            <CollapsiblePrompt prompt={experiment.prompt} />
          ) : null}
          <DetailField label="Execution">{executionLabel(summary)}</DetailField>
          <DetailField label="Cohort">
            <div className="flex flex-wrap items-center gap-2">
              <span>
                {summary.cohort?.name ?? 'Additional emails only'} ·{' '}
                {summary.target_count} targeted
                {summary.removed_count
                  ? ` · ${summary.removed_count} removed`
                  : ''}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={revoked}
                onClick={onEditCohort}
              >
                <Users />
                Edit cohort
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={revoked || sync.isPending}
                onClick={syncCohort}
              >
                <RefreshCw />
                {sync.isPending ? 'Syncing...' : 'Sync from PostHog'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={revoked || setPaused.isPending}
                onClick={() =>
                  setPaused.mutate({
                    experimentId: summary.id,
                    paused: !paused,
                  })
                }
              >
                {paused ? <Play /> : <Pause />}
                {paused ? 'Resume' : 'Pause'}
              </Button>
            </div>
            {sync.error || setPaused.error ? (
              <p role="alert" className="mt-1 text-destructive">
                {(sync.error ?? setPaused.error)?.message}
              </p>
            ) : null}
          </DetailField>

          <DetailField label="Created by">{summary.created_by}</DetailField>
          <DetailField label="Created">
            {new Date(summary.created_at).toLocaleString()}
          </DetailField>
          <DetailField label="Issuance">
            {summary.issued_count} issued · {summary.failed_count} failed ·{' '}
            {summary.pending_count} pending
            {summary.revoke_requested_at ? ' · revoke requested' : ''}
          </DetailField>
        </dl>
      </section>

      <section className="space-y-4">
        <h3 className="text-sm font-semibold">Recipients</h3>
        {recipients.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recipients yet.</p>
        ) : (
          <ProactivityExperimentRecipientLists
            deliveryKind={summary.delivery_kind}
            recipients={recipients}
          />
        )}
      </section>
    </div>
  );
};
